import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AsH2, Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useExperimentContext } from '@/experimentation'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { EnhancedShowtimeProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/EnhancedShowtimeContext'
import ExclusiveShowtimeDateWarning from '@/views/TicketCheckoutViews/ShowtimesView/components/ExclusiveShowtimeDateWarning'
import VenueList, { LocaleWarningModalState } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueList'
import VenueListSkeleton from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueListSkeleton'
import { GroupTicketsModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal'
import LocalWarningModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/LocalWarningModal'
import { useTheatricalPromotionContext } from '../../components/TheatricalPromotionContext'
import useSortedVenues from '../hooks/useSortedVenues'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { InitializedReservation } from '../initReservation'
import VenuesSoldOutAlert from './VenuesSoldOutAlert'

interface Props {
  venues: ShowtimesVenue[]
  theatricalRelease: TheatricalReleaseResult
  isLoading: boolean
  reservation: InitializedReservation
  isExclusiveDateSelected?: boolean
}

const Venues: FC<Props> = ({ venues, theatricalRelease, isLoading, reservation, isExclusiveDateSelected }) => {
  const { t } = useTranslate('tickets')
  const { isReady, query } = useSafeRouter()
  const [isGroupTicketsModalOpen, setIsGroupTicketsModalOpen] = useState(false)
  const soldOut =
    theatricalRelease?.soldOutShowtimeCount && theatricalRelease.soldOutShowtimeCount > 0
      ? theatricalRelease.soldOutShowtimeCount
      : undefined
  const { track } = useSafeAnalytics()
  const reservationId = reservation?.id
  const { promo } = useTheatricalPromotionContext()

  const theatricalSlug = theatricalRelease?.theatricalSlug

  const { venuesWithShowtimes, venuesWithNoShowtimes } = useSortedVenues({
    venues,
  })

  const openGroupTicketsModal = useCallback(() => {
    setIsGroupTicketsModalOpen(true)
  }, [])

  useEffect(() => {
    if (query?.highlightGroupTickets || query?.groupDiscountCode) {
      openGroupTicketsModal()
    }
  }, [openGroupTicketsModal, query?.highlightGroupTickets, query?.groupDiscountCode])

  const [localeWarningModalState, setLocaleWarningModalState] = useState<LocaleWarningModalState>({
    isOpen: false,
    warning: '',
    callback: () => null,
  })

  const closeLocaleWarningModal = useCallback(() => {
    track('Canceled Showtime From Language Modal')
    setLocaleWarningModalState({ isOpen: false, warning: '', callback: () => null })
  }, [track])

  const mainPromoIsGuild = promo?.promoType === 'premium_guild'

  const { getFeatureValue } = useExperimentContext()

  const shouldHideEmptyState = useMemo(() => {
    if (isLoading || !isReady || venuesWithShowtimes?.length !== 0 || theatricalSlug !== slugs.soloMio) return false
    return getFeatureValue('solo-mio-hide-no-showtimes-message', false)
  }, [theatricalSlug, getFeatureValue, venuesWithShowtimes?.length, isLoading, isReady])

  if (!theatricalSlug) return null
  if (isLoading || !isReady) return <VenueListSkeleton />

  return (
    <EnhancedShowtimeProvider venues={venues}>
      <FlexColumn className="min-h-[50vh] gap-6 md:min-h-[55vh]">
        <FlexColumn className="gap-4">
          {isExclusiveDateSelected && <ExclusiveShowtimeDateWarning theatricalSlug={theatricalSlug} />}

          {soldOut && soldOut > 9 && venuesWithShowtimes?.length > 0 && <VenuesSoldOutAlert soldOut={soldOut} />}

          <VenueList
            venues={venuesWithShowtimes}
            theatricalRelease={theatricalRelease}
            theaterSlug={theatricalSlug}
            setLocaleWarningModalState={setLocaleWarningModalState}
            isGroupTicketsModalOpen={isGroupTicketsModalOpen}
            openGroupTicketsModal={openGroupTicketsModal}
            reservationId={reservationId}
            theatricalSlug={theatricalSlug}
            showGuildTicketBanner={!mainPromoIsGuild}
            showGiveawayEmptyState
            shouldHideEmptyState={shouldHideEmptyState}
          />
        </FlexColumn>

        {venuesWithNoShowtimes?.length > 0 && !isExclusiveDateSelected && (
          <FlexColumn className="experiment-hidden gap-4">
            {!shouldHideEmptyState && (
              <Text as={AsH2} className="photon-title-md md:photon-title-lg" weight="bold">
                {t('noAvailableShowtimes', 'No Available Showtimes')}
              </Text>
            )}
            <VenueList
              venues={venuesWithNoShowtimes}
              theatricalRelease={theatricalRelease}
              theaterSlug={theatricalSlug}
              setLocaleWarningModalState={setLocaleWarningModalState}
              isGroupTicketsModalOpen={isGroupTicketsModalOpen}
              openGroupTicketsModal={openGroupTicketsModal}
              reservationId={reservationId}
              theatricalSlug={theatricalSlug}
              showGuildTicketBanner={Boolean(!venuesWithShowtimes?.length) && !mainPromoIsGuild}
              showGiveawayEmptyState={Boolean(!venuesWithShowtimes?.length)}
            />
          </FlexColumn>
        )}
      </FlexColumn>
      <LocalWarningModal onClose={closeLocaleWarningModal} localeWarningModalState={localeWarningModalState} />
      {isGroupTicketsModalOpen && theatricalRelease?.groupProductVariantId && (
        <GroupTicketsModal
          theatricalSlug={theatricalSlug}
          open={isGroupTicketsModalOpen}
          onClose={() => setIsGroupTicketsModalOpen(false)}
          groupTicketProductVariantId={theatricalRelease.groupProductVariantId}
        />
      )}
    </EnhancedShowtimeProvider>
  )
}

export default Venues
